/**
 * Audit Log "Table settings" cog menu (storyboard Flow J; the table-
 * settings enhancement).
 *
 * A Radix Popover anchored to a gear icon in the panel header's top-
 * right, matching the Meridian house menu pattern (mirrors
 * :func:`preferences/preferences-menu` + :func:`entity-details/
 * section-visibility-menu` — Radix Popover + a collapsible section, NOT
 * a hand-rolled dropdown). Contents, top to bottom:
 *
 * 1. **Show Columns** — a collapsible section (``aria-expanded`` /
 *    ``aria-controls``) of checkboxes toggling which columns the table
 *    shows. The **min-1-column** invariant is enforced upstream
 *    (:func:`audit-display.toggleColumnKey`); the last-visible column's
 *    checkbox renders disabled so the operator can see why it won't
 *    toggle.
 * 2. **Export…** — an action item that opens the existing
 *    :func:`audit-export-modal` (export was relocated here from the
 *    filter bar; behaviour is unchanged — it still covers the active
 *    filters, never the visible-columns or sort).
 *
 * Cross-references:
 *   - Flow J Frame 1 (table + export entry point) / Frame 4 (export modal).
 *   - REQ-UI-022 (Audit Log surface); REQ-UI-059 (keyboard-navigable
 *     menu + Enter/Space-operable affordances).
 */
import * as Popover from "@radix-ui/react-popover";
import { CaretDown, Export, Gear } from "@phosphor-icons/react";
import { useState } from "react";
import { AUDIT_COLUMNS } from "@/audit/audit-display";
import { cn } from "@/lib/utils";

export interface AuditColumnSettingsMenuProps {
  /** Currently-visible column keys (the panel owns the set). */
  visibleCols: ReadonlySet<string>;
  /** Toggle one column key (panel enforces the min-1 invariant + persists). */
  onToggleColumn: (key: string) => void;
  /** Open the export modal (relocated from the filter bar). */
  onExport: () => void;
}

/**
 * Render the cog menu. Stateful only for the popover open + the Show-
 * Columns submenu expansion; column visibility + export both delegate to
 * the panel via props.
 */
export function AuditColumnSettingsMenu({
  visibleCols,
  onToggleColumn,
  onExport,
}: AuditColumnSettingsMenuProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [showColumns, setShowColumns] = useState(false);

  // The last-visible column cannot be hidden (min-1 invariant). Its
  // checkbox is disabled so the affordance reads as "can't remove this"
  // rather than silently no-op'ing on click.
  const onlyOneVisible = visibleCols.size <= 1;

  const handleOpenChange = (next: boolean): void => {
    setOpen(next);
    if (!next) setShowColumns(false);
  };

  const handleExport = (): void => {
    // Close the popover so the operator sees the modal over the canvas.
    setOpen(false);
    setShowColumns(false);
    onExport();
  };

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label="Table settings"
          title="Table settings"
          className="rounded p-1 text-text-secondary hover:bg-bg-elevated hover:text-text-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-accent"
          data-testid="audit-settings-gear"
        >
          <Gear size={16} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="end"
          sideOffset={6}
          className={cn(
            "z-50 min-w-[200px] overflow-hidden rounded-md",
            "border border-border bg-bg-overlay shadow-lg",
          )}
          data-testid="audit-settings-menu"
          aria-label="Table settings"
        >
          {/* Show Columns — collapsible checkbox section. */}
          <button
            type="button"
            onClick={() => setShowColumns((prev) => !prev)}
            aria-expanded={showColumns}
            aria-controls="audit-settings-columns"
            data-testid="audit-settings-show-columns"
            className={cn(
              "flex w-full items-center justify-between gap-2 px-4 py-2.5",
              "text-body-sm text-text-primary hover:bg-bg-elevated",
              "focus:outline-none focus:bg-bg-elevated",
            )}
          >
            <span>Show Columns</span>
            <CaretDown
              size={12}
              aria-hidden="true"
              className={cn("transition-transform", showColumns && "rotate-180")}
            />
          </button>
          {showColumns && (
            <div
              id="audit-settings-columns"
              role="group"
              aria-label="Visible columns"
              data-testid="audit-settings-columns"
              className="border-t border-border px-2 py-1"
            >
              {AUDIT_COLUMNS.map((col) => {
                const checked = visibleCols.has(col.key);
                // Disable un-checking the last column (min-1 invariant).
                const disabled = checked && onlyOneVisible;
                return (
                  <label
                    key={col.key}
                    className={cn(
                      "flex items-center gap-2 rounded px-2 py-1.5 text-body-sm",
                      disabled
                        ? "cursor-not-allowed text-text-disabled"
                        : "cursor-pointer text-text-primary hover:bg-bg-elevated",
                    )}
                    data-testid={`audit-settings-col-${col.key}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => onToggleColumn(col.key)}
                      data-testid={`audit-settings-col-checkbox-${col.key}`}
                      className="h-3.5 w-3.5 cursor-pointer disabled:cursor-not-allowed accent-accent"
                    />
                    <span>{col.label}</span>
                  </label>
                );
              })}
            </div>
          )}

          {/* Export… — opens the relocated export modal (Flow J Frame 4). */}
          <div className="border-t border-border" />
          <button
            type="button"
            onClick={handleExport}
            data-testid="audit-settings-export"
            className={cn(
              "flex w-full items-center gap-2 px-4 py-2.5",
              "text-body-sm text-text-primary hover:bg-bg-elevated",
              "focus:outline-none focus:bg-bg-elevated",
            )}
          >
            <Export size={14} aria-hidden="true" />
            <span>Export…</span>
          </button>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
